import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { VolumeOffRounded as QuietIcon, VolumeUpRounded as VoiceIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import api from "../../../api";
import { parseJWTToken } from "../../../helpers";
import { useSetSessionLanguage } from "../../../components/RespondentIntlProvider";
import ReviewScreen from "./ReviewScreen";
import SurveyCompletion from "../../../components/Survey/SurveyCompletion";
import { backgroundDimming } from "../../../components/Survey/surveyStyle";
import VoiceQuestionScreen from "./VoiceQuestionScreen";
import VoiceSurveyFormScreen, { ChoiceAnswer } from "./VoiceSurveyFormScreen";
import VoiceIntroScreen, { VoiceWelcome } from "./VoiceWelcome";
import { surveyInterviewTheme } from "./surveyInterviewTheme";
import useVoiceSurveyController from "./useVoiceSurveyController";
import { answerItem, answerItems, buildVoiceStages, isVoiceQuestion, missingRequired, questionText } from "./voiceSurveyFlow";

// The respondent runner for a VOICE-mode ("Modern") survey
// (Survey.respond_mode === "voice").
//
// The survey's stages, in order, from the first item (see buildVoiceStages: pages
// cut at page breaks, every open-text question on a screen of its own) → review
// of every answer (spoken ones editable or re-recorded, choices re-picked) → submit → the survey's own confirmation page
// (SurveyCompletion, as the traditional runner ends).
//
// The look is the survey's appearance settings (surveyInterviewTheme), the same
// ones the traditional runner reads.
//
// The survey opens with a short greeting (VoiceWelcome): above the first page's
// questions, or on a screen of its own when the survey opens on an open-text
// question (which needs the whole screen) or has nothing in it. Its Continue
// unlocks audio for the first voice; a first page's Continue does the same, and
// a browser that still blocks it shows "Tap to enable sound" (the text and the
// turn carry on regardless).
//
// A Quiet mode toggle sits at the top of every screen: in it, open-text questions
// still get their own screen but show in full, unread (Replay still reads one).
//
// `preview` is the builder's Preview tab: the same screens over the unsaved
// steps, read in `survey.voice` / `survey.language` through the signed-in
// preview routes; nothing is saved or tracked, and Submit does nothing (the
// confirmation page is the builder's own Submission preview).
//
// Every answer is an ordinary survey answer: each is saved as it is given
// (POST /surveys/<id>/save/<token>/, the classic runner's own endpoint), and the
// final submit sends them all again with is_submit, so a save that failed along
// the way is never lost. The report pipeline can't tell this from a typed survey.
export default function VoiceSurveyRunner({ survey, steps, surveyId, token, preview = false }) {
    const intl = useIntl();
    const settings = survey?.settings;
    const t = useMemo(() => surveyInterviewTheme(settings || {}), [settings]);
    const setSessionLanguage = useSetSessionLanguage();
    const stages = useMemo(() => buildVoiceStages(steps), [steps]);
    const voiceQuestions = useMemo(() => (steps || []).filter(isVoiceQuestion), [steps]);
    const allQuestions = useMemo(() => stages.flatMap((s) => s.questions), [stages]);

    // The greeting has a screen of its own unless the survey opens on a page.
    const introScreen = stages[0]?.kind !== "page";
    // "intro" | "stage" | "review" | "done"
    const [phase, setPhase] = useState(introScreen ? "intro" : "stage");
    const [stageIndex, setStageIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const answersRef = useRef(answers);
    answersRef.current = answers;
    const [busy, setBusy] = useState(false);
    const [quiet, setQuiet] = useState(false);
    const [error, setError] = useState("");

    // The survey's language drives the respondent UI
    // (a ?lang= on the link still wins — see RespondentIntlProvider).
    useEffect(() => {
        if (survey?.language && setSessionLanguage) setSessionLanguage(survey.language);
    }, [survey?.language, setSessionLanguage]);

    const setAnswer = useCallback((questionId, value) => {
        setAnswers((prev) => ({ ...prev, [questionId]: value }));
    }, []);

    // Best-effort save of some answers as they are given (never blocks the flow).
    const saveSome = useCallback(
        async (items) => {
            if (preview || !items.length) return;
            try {
                await api.saveSurveyResponse(surveyId, token, items, false);
            } catch (e) {
                /* re-sent by the final submit */
            }
        },
        [surveyId, token, preview]
    );

    // Next on an open-text question: its answer, or none (an optional one left
    // blank, or cleared after going back).
    const onVoiceAnswer = useCallback(
        async (step, text) => {
            setAnswer(step.id, text);
            const item = answerItem(step, text);
            if (item) await saveSome([item]);
        },
        [setAnswer, saveSome]
    );
    const getAnswer = useCallback((step) => answersRef.current[step.id], []);

    const voice = survey?.voice;
    const language = survey?.language;
    const previewSynthesize = useCallback(
        (text, signal) => api.fetchSurveyPreviewTts(text, voice, language, signal),
        [voice, language]
    );
    const previewTranscribe = useCallback((blob) => api.transcribePreviewAudio(blob, language), [language]);

    const controller = useVoiceSurveyController({
        surveyId,
        token,
        onAnswer: onVoiceAnswer,
        getAnswer,
        synthesize: preview ? previewSynthesize : undefined,
        transcribeAudio: preview ? previewTranscribe : undefined,
        quiet,
    });

    const submit = useCallback(async () => {
        setError("");
        const missing = missingRequired(allQuestions, answersRef.current);
        if (missing.length > 0) {
            setError(intl.formatMessage({ id: "voice_survey.required_missing", defaultMessage: "Please answer every required question before submitting." }));
            return;
        }
        // The builder's preview never submits: its confirmation page is the
        // builder's own Submission preview.
        if (preview) return;
        setBusy(true);
        try {
            await api.saveSurveyResponse(surveyId, token, answerItems(allQuestions, answersRef.current), true);
            setPhase("done");
        } catch (e) {
            setError(intl.formatMessage({ id: "voice_survey.submit_failed", defaultMessage: "We couldn't submit your answers. Please try again." }));
        } finally {
            setBusy(false);
        }
    }, [allQuestions, surveyId, token, intl, preview]);

    // Move to stage `i`, or on past the last one: the review. `fromBack` enters a voice stage at its
    // last question. Answers are kept, so every screen shows them again.
    const goToStage = useCallback(
        (i, { fromBack = false } = {}) => {
            controller.stopReading(); // a content block's voice ends with its page
            if (i >= stages.length) {
                setPhase("review");
                return;
            }
            setStageIndex(i);
            setPhase("stage");
            if (stages[i].kind === "voice") {
                controller.runStage(stages[i].questions, {
                    onDone: () => goToStage(i + 1),
                    onBack: i > 0 ? () => goToStage(i - 1, { fromBack: true }) : () => setPhase("intro"),
                    startAt: fromBack ? stages[i].questions.length - 1 : 0,
                });
            }
            // A page before a voice stage warms that stage's first question.
            else if (stages[i + 1]?.kind === "voice") controller.prepare(stages[i + 1].questions[0]);
        },
        [stages, controller]
    );

    // Into the first item, or (greeting on its own screen) warm the first
    // question's voice while it shows. Re-running it (React's dev double-mount)
    // just starts over.
    useEffect(() => {
        if (!introScreen) goToStage(0);
        else if (stages[0]) controller.prepare(stages[0].questions[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A public link counts as opened when it is, as the classic runner counts it.
    const trackedRef = useRef(false);
    useEffect(() => {
        if (preview || trackedRef.current) return;
        trackedRef.current = true;
        try {
            const data = parseJWTToken(token);
            if (data?.type === "public_link" && data.collector_id) api.trackSurveyOpen(data.collector_id, token).catch(() => {});
        } catch (e) {
            /* tracking never blocks the survey */
        }
    }, [preview, token]);

    // The survey's page behind every screen: its background (dimmed per its
    // brightness setting), and its logo where the author placed it. A respondent's
    // page fills the window; the builder's preview fills its pane (never the
    // window's height, so it doesn't scroll through empty background).
    const dim = backgroundDimming(settings || {});
    const frame = (children) => (
        <Box data-testid="voice-survey-frame" sx={{ ...t.pageBackground, ...(preview && { minHeight: 0, flexGrow: 1 }), display: "flex", flexDirection: "column" }}>
            {dim > 0 && (
                <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, backgroundColor: "#000", opacity: dim, pointerEvents: "none" }} />
            )}
            {/* The logo where the author placed it, and the Quiet mode toggle */}
            <Box sx={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", gap: 2, px: { xs: 2, sm: 4 }, pt: 1.5 }}>
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        justifyContent: t.logoAlignment === "center" ? "center" : t.logoAlignment === "right" ? "flex-end" : "flex-start",
                    }}
                >
                    {t.logo && (
                        <img
                            src={t.logo}
                            alt="Logo"
                            style={{ maxWidth: "clamp(80px, 20vw, 150px)", maxHeight: "clamp(40px, 10vw, 75px)", objectFit: "contain" }}
                        />
                    )}
                </Box>
                <Button
                    size="small"
                    variant={quiet ? "contained" : "outlined"}
                    disableElevation
                    aria-pressed={quiet}
                    onClick={() => setQuiet((q) => !q)}
                    startIcon={quiet ? <QuietIcon /> : <VoiceIcon />}
                    data-testid="voice-quiet"
                    sx={{
                        flexShrink: 0,
                        textTransform: "none",
                        borderRadius: "999px",
                        px: 1.75,
                        fontWeight: 600,
                        color: quiet ? t.formBtnText || "#fff" : t.kicker,
                        borderColor: t.pauseBorder,
                        backgroundColor: quiet ? t.formBtnBg : "transparent",
                        "&:hover": { borderColor: t.kicker, backgroundColor: quiet ? t.formBtnHover : t.cueBg },
                    }}
                >
                    <FormattedMessage id="voice_survey.quiet_mode" defaultMessage="Quiet mode" />
                </Button>
            </Box>
            <Box sx={{ position: "relative", zIndex: 1, flexGrow: 1, display: "flex", flexDirection: "column" }}>{children}</Box>
        </Box>
    );
    const errorBanner = error ? (
        <Alert severity="error" sx={{ position: "fixed", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 1400, maxWidth: "calc(100% - 32px)" }} onClose={() => setError("")}>
            {error}
        </Alert>
    ) : null;

    if (phase === "done") return <SurveyCompletion settings={settings || {}} />;

    if (phase === "review") {
        return frame(
            <>
                <ReviewScreen
                    items={allQuestions.map((q) =>
                        isVoiceQuestion(q)
                            ? { turn_id: q.id, question: questionText(q), answer: answers[q.id] || "" }
                            : {
                                  turn_id: q.id,
                                  question: questionText(q),
                                  answerNode: (
                                      <ChoiceAnswer
                                          q={q}
                                          value={answers[q.id]}
                                          labelId={`review-q-${q.id}`}
                                          onChange={(id, value) => {
                                              setAnswer(id, value);
                                              saveSome(answerItems([q], { [id]: value }));
                                          }}
                                          t={t}
                                      />
                                  ),
                              }
                    )}
                    surveyId={surveyId}
                    token={token}
                    transcribeAudio={preview ? previewTranscribe : undefined}
                    showComments={false}
                    onEditTurn={async (questionId, text) => {
                        const step = voiceQuestions.find((q) => q.id === questionId);
                        if (step?.is_required && !text.trim()) throw new Error("required");
                        setAnswer(questionId, text);
                        const item = answerItem(step, text);
                        if (item && !preview) await api.saveSurveyResponse(surveyId, token, [item], false);
                    }}
                    onComplete={submit}
                    submitLabel={settings?.submitButtonText || undefined}
                    singleSubmit
                    topBar={false}
                    hint={
                        <FormattedMessage
                            id="voice_survey.review_hint"
                            defaultMessage="Here are all your answers. Change any of them here: edit or re-record what you said, or pick a different option. When you're happy with them, submit."
                        />
                    }
                    onBack={stages.length ? () => goToStage(stages.length - 1, { fromBack: true }) : undefined}
                    backLabel={settings?.backButtonText || undefined}
                    t={t}
                />
                {errorBanner}
            </>
        );
    }

    if (phase === "intro") {
        return frame(
            <VoiceIntroScreen
                t={t}
                nextLabel={settings?.nextButtonText || undefined}
                onNext={
                    stages.length
                        ? () => {
                              controller.unlock(); // in the click, so the first voice may play
                              goToStage(0);
                          }
                        : undefined
                }
            />
        );
    }

    const stage = stages[stageIndex];
    const numberFrom = stages.slice(0, stageIndex).reduce((n, s) => n + s.questions.length, 0) + 1;
    if (stage?.kind === "page") {
        return frame(
            <>
                <VoiceSurveyFormScreen
                    key={stageIndex}
                    items={stage.items}
                    questions={stage.questions}
                    answers={answers}
                    onChange={setAnswer}
                    busy={busy}
                    numberFrom={numberFrom}
                    readingId={controller.readingId}
                    onReadAloud={controller.readAloud}
                    onStopReading={controller.stopReading}
                    welcome={stageIndex === 0 ? <VoiceWelcome t={t} /> : null}
                    continueLabel={settings?.nextButtonText || undefined}
                    backLabel={settings?.backButtonText || undefined}
                    onBack={
                        stageIndex > 0
                            ? () => {
                                  controller.unlock(); // in the click, so the voice before may play
                                  goToStage(stageIndex - 1, { fromBack: true });
                              }
                            : undefined
                    }
                    onContinue={async () => {
                        controller.unlock(); // in the click, so the next voice may play
                        setBusy(true);
                        await saveSome(answerItems(stage.questions, answersRef.current));
                        setBusy(false);
                        goToStage(stageIndex + 1);
                    }}
                    t={t}
                />
                {errorBanner}
            </>
        );
    }

    // A voice stage, shown once its question's voice is ready (until then, just
    // the survey's page and a spinner). Progress is across the whole survey.
    if (!stage || (stage.kind === "voice" && !controller.ready)) {
        return frame(
            <Box data-testid="voice-preparing" sx={{ flexGrow: 1, minHeight: "60vh", display: "grid", placeItems: "center" }}>
                <CircularProgress size={28} sx={{ color: t.progressFill }} />
            </Box>
        );
    }
    const progress = allQuestions.length ? (numberFrom - 1 + controller.index) / allQuestions.length : 0;
    return frame(
        <>
            <VoiceQuestionScreen
                key={`${stageIndex}-${controller.index}`}
                question={controller.question}
                required={!!controller.current?.is_required}
                progress={progress}
                orbMode={controller.orbMode}
                micStatus={controller.micStatus}
                draft={controller.draft}
                onDraftChange={controller.setDraft}
                onBack={controller.back} // the first question goes back to the greeting
                onNext={controller.next}
                nextLabel={settings?.nextButtonText || undefined}
                backLabel={settings?.backButtonText || undefined}
                recordLabel={settings?.recordButtonText || undefined}
                onStartRecording={controller.startRecording}
                onSubmitRecording={controller.submitRecording}
                onStopSpeaking={controller.stopSpeaking}
                onReplay={controller.replay}
                turnInFlight={controller.turnInFlight}
                soundBlocked={controller.soundBlocked}
                onEnableSound={controller.enableSound}
                ttsLevelRef={controller.ttsLevelRef}
                micAnalyserRef={controller.micAnalyserRef}
                ttsStartedRef={controller.ttsStartedRef}
                ttsProgressRef={controller.ttsProgressRef}
                ttsNoAudioRef={controller.ttsNoAudioRef}
                utteranceKey={controller.utteranceKey}
                voiced={controller.voiced}
                quiet={quiet}
                t={t}
            />
            {errorBanner}
        </>
    );
}
